// The site plan's access lines, pure: a stroke drawn with a finger or the
// Pencil becomes one line (simplified, in plan coordinates 0–1), lines are
// added to and taken off a visit area's hig_accesslines as a change to the
// stored copy (so two quick edits never save over each other), and the same
// smooth curve is drawn on screen (SVG) and into Site plan (access).jpg
// (canvas). The rules both the iPad and the API share are in
// @higgins/condition-library (access.js). Design: docs/CONDITION-REGION.md "Access plan".
import { ACCESS_LINE_EDGE, accessLineShape, accessLinesJson, accessType, distanceToPolyline, parseAccessLines, simplifyStroke, smoothCurve, type AccessLine, type AccessTypeKey, type PlanPoint } from '@higgins/condition-library';
import type { DeviceRecord, Fields } from './device-db';

/** hig_accesslines' limit on one line's points (access.js MAX_POINTS). */
const MAX_POINTS = 400;
/** Places kept on a point (0–1): a ten-thousandth of the plan, as a pin's place is kept. */
const PLACES = 10000;
/** A stroke shorter than this many line widths is a tap, not a line. */
const SHORTEST_LINE_WIDTHS = 1.5;

interface PlanSize {
  width: number;
  height: number;
}

const round = (n: number) => Math.round(Math.min(1, Math.max(0, n)) * PLACES) / PLACES;

const strokeLength = (points: readonly PlanPoint[]) => points.slice(1).reduce((sum, p, i) => sum + Math.hypot(p[0] - points[i][0], p[1] - points[i][1]), 0);

/**
 * A stroke (plan pixels, as drawn) as a line to keep: its jitter simplified
 * away, then 0–1 across and down the plan. Null for a tap or a stroke too
 * short to be a line.
 */
export function strokeToLine(stroke: readonly PlanPoint[], plan: PlanSize, type: AccessTypeKey, id: string): AccessLine | null {
  const shape = accessLineShape(plan.width);
  if (stroke.length < 2 || strokeLength(stroke) < shape.width * SHORTEST_LINE_WIDTHS) return null;
  let tolerance = shape.simplify;
  let points = simplifyStroke(stroke, tolerance);
  while (points.length > MAX_POINTS) points = simplifyStroke(stroke, (tolerance *= 2));
  return { id, type, points: points.map(([x, y]) => [round(x / plan.width), round(y / plan.height)]) };
}

/** How a stroke is drawn (#613): following the finger (curved elevations), or straight between its two ends. */
export type DrawTool = 'freehand' | 'straight';

/** The stroke so far with the finger now at `next`: every point freehand; a straight line keeps only where it started and `next`. */
export function extendStroke(points: readonly PlanPoint[], next: PlanPoint, tool: DrawTool): PlanPoint[] {
  return tool === 'straight' ? [points[0], next] : [...points, next];
}

/** A line's points in plan pixels. */
export const linePixels = (line: AccessLine, plan: PlanSize): PlanPoint[] => line.points.map(([x, y]) => [x * plan.width, y * plan.height]);

/** The change that adds a line to a visit area's stored lines. */
export const withLineAdded =
  (line: AccessLine) =>
  (stored: Fields): Fields => ({ hig_accesslines: accessLinesJson([...parseAccessLines(stored.hig_accesslines), line]) });

/** The change that takes one line off a visit area's stored lines (nothing, if it's gone already). */
export const withLineRemoved =
  (lineId: string) =>
  (stored: Fields): Fields => {
    const lines = parseAccessLines(stored.hig_accesslines);
    return lines.some((l) => l.id === lineId) ? { hig_accesslines: accessLinesJson(lines.filter((l) => l.id !== lineId)) } : {};
  };

/** One line on the plan, with the External area it is drawn along. */
export interface PlacedLine {
  visitAreaId: string;
  /** The job's area (hig_measurearea) the visit area is for. */
  areaId: string;
  areaName: string;
  line: AccessLine;
}

/**
 * Each visit area's side as its job area has it now: the visit area's own copy is made once and isn't
 * kept in step when the area is switched between Internal and External. A visit area whose job area has
 * been removed has no side (its lines are left off). The server does the same (api access-plan.ts).
 */
export function currentSides(records: readonly DeviceRecord[]): (visitArea: DeviceRecord) => unknown {
  const areas = new Map(records.filter((r) => r.table === 'area').map((a) => [a.id, a.fields.hig_side]));
  return (visitArea) => {
    const areaId = visitArea.fields.hig_measureareaid;
    return typeof areaId === 'string' ? (areas.get(areaId) ?? null) : visitArea.fields.hig_side;
  };
}

/** Every External area's lines on the visit, in the job's area order. */
export function accessLinesOf(records: readonly DeviceRecord[]): PlacedLine[] {
  const sideOf = currentSides(records);
  return records
    .filter((r) => r.table === 'visitarea' && sideOf(r) === 'External')
    .sort((a, b) => Number(a.fields.hig_sortorder ?? 0) - Number(b.fields.hig_sortorder ?? 0))
    .flatMap((v) => parseAccessLines(v.fields.hig_accesslines).map((line) => ({ visitAreaId: v.id, areaId: String(v.fields.hig_measureareaid ?? ''), areaName: String(v.fields.hig_name ?? ''), line })));
}

/** The line a tap at `point` (plan pixels) is on: the nearest within `reach`, the one drawn last when two are as near. Null when none. */
export function lineAt(lines: readonly PlacedLine[], point: PlanPoint, plan: PlanSize, reach: number): PlacedLine | null {
  let found: PlacedLine | null = null;
  let best = reach;
  for (const placed of lines) {
    const d = distanceToPolyline(point, linePixels(placed.line, plan));
    if (d <= best) [found, best] = [placed, d];
  }
  return found;
}

/** A line's smooth curve as an SVG path, in plan pixels. */
export function curvePath(points: readonly PlanPoint[]): string {
  if (!points.length) return '';
  const [first] = points;
  return [`M ${first[0]} ${first[1]}`, ...smoothCurve(points).map((s) => `C ${s.c1[0]} ${s.c1[1]} ${s.c2[0]} ${s.c2[1]} ${s.to[0]} ${s.to[1]}`)].join(' ');
}

/** What drawing a line needs of a canvas (CanvasRenderingContext2D; a recording fake in tests). */
export type LineCanvas = Pick<CanvasRenderingContext2D, 'beginPath' | 'moveTo' | 'bezierCurveTo' | 'stroke' | 'lineWidth' | 'strokeStyle' | 'lineCap' | 'lineJoin'>;

/** Stroke one curve at a width and colour, round-capped. */
function strokeCurve(ctx: LineCanvas, points: readonly PlanPoint[], width: number, colour: string): void {
  ctx.beginPath();
  ctx.moveTo(points[0][0], points[0][1]);
  for (const s of smoothCurve(points)) ctx.bezierCurveTo(s.c1[0], s.c1[1], s.c2[0], s.c2[1], s.to[0], s.to[1]);
  ctx.lineWidth = width;
  ctx.strokeStyle = colour;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.stroke();
}

/** Draw access lines on a plan: each its outline first, then its colour over it, as on screen. */
export function drawAccessLines(ctx: LineCanvas, plan: PlanSize, lines: readonly AccessLine[]): void {
  const shape = accessLineShape(plan.width);
  for (const line of lines) {
    const type = accessType(line.type);
    if (!type || line.points.length < 2) continue;
    const points = linePixels(line, plan);
    strokeCurve(ctx, points, shape.width + 2 * shape.edge, ACCESS_LINE_EDGE);
    strokeCurve(ctx, points, shape.width, type.colour);
  }
}
